import {
    BrandedHeader,
    BrandingProvider
} from '@components/branding';
import LanguageSelector from '@components/LanguageSelector';
import PageViewTracker from '@components/page-view-tracker';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import Routes from '@models/routes';
import ErrorPage from '@screens/error-page';
import CompleteFormI9Section2 from '@screens/review-section2/complete-i9';
import I9DocumentReview from '@screens/review-section2/document-review';
import I9DocumentUpload from '@screens/review-section2/document-upload';
import I9EmployeeInformationReview from '@screens/review-section2/employee-information';
import Section2ThankYou from '@screens/review-section2/thank-you';
import PinCodeAuthorisation from '@screens/review-section3/pincode-authorisation';
import Review from '@screens/review-section3/review';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import {
    createApplicationInfoInterceptor,
    createDocumentDetailsChangeTrackingService,
    createSection1CandidateDetailUpdater,
    createSection1PreparerTranslatorUpdater,
    createSection2CertificationUpdater,
    createSection2DocumentsUpdater
} from '@services/factories';
import { IApplicationInfoInterceptor } from '@services/http/application-info-interceptor';
import { IDocumentDetailsChangeTrackingService } from '@services/onboarding-review/document-details-change-tracking-service';
import { AuthenticationParamsProvider } from '@stores/authentication/authentication-params-context';
import { AuthorisationProvider } from '@stores/authorisation/authorisation-context';
import { ISection1CandidateDetailUpdater } from '@stores/onboarding-review/section1-candidate-detail-updater';
import { ISection1PreparerTranslatorUpdater } from '@stores/onboarding-review/section1-preparer-translator-updater';
import { ISection2CertificationUpdater } from '@stores/onboarding-review/section2-certification-updater';
import { ISection2DocumentsUpdater } from '@stores/onboarding-review/section2-documents-updater';
import { registerAppConstantsProvider } from '@swipejobs/frontend-theme-provider';
import {
    ComponentAndPropEntry,
    ComposeProviders
} from '@utils/compose-providers';
import i18n from 'i18next';
import * as React from 'react';
import {
    BrowserRouter,
    Route,
    Switch
} from 'react-router-dom';
import Loader from './components/loader';
import AuthorisedRoute from './containers/authorised-route';
import NotFoundContainer from './containers/NotFound';
import ConfigurationValuesService, { IConfigurationValuesService } from './services/ConfigurationValuesService';
import TrackingService, { ITrackingService } from './services/TrackingService';
import TranslationService from './services/TranslationService';

export interface Props {
    location?: {
        pathname: string;
    };
}

export interface State {
    error: boolean;
    loaded: boolean;
}

class App extends React.Component<Props, State> {
    private trackingService: ITrackingService | undefined;
    private configurationValuesService: IConfigurationValuesService | undefined;

    // Probably good idea to dependency inject these later
    candidateDetailsUpdater: ISection1CandidateDetailUpdater = createSection1CandidateDetailUpdater();
    preparerTranslatorUpdater: ISection1PreparerTranslatorUpdater = createSection1PreparerTranslatorUpdater();
    certificationUpdater: ISection2CertificationUpdater = createSection2CertificationUpdater();
    documentsUpdater: ISection2DocumentsUpdater = createSection2DocumentsUpdater();
    applicationInfoInterceptor: IApplicationInfoInterceptor = createApplicationInfoInterceptor();
    documentChangeTrackingService: IDocumentDetailsChangeTrackingService = createDocumentDetailsChangeTrackingService();

    static extractBasenameFromPath(routePath: string) {
        const pathName = window.location.pathname;
        return pathName.replace(routePath, '') || '/';
    }

    constructor(props: any) {
        super(props);
        this.state = {
            error: false,
            loaded: false
        };
    }

    async componentDidMount() {
        await this.initAsync();
        this.applicationInfoInterceptor.initialise();
        // logic layer: temporary place to initialise the updaters
        this.candidateDetailsUpdater.initialise();
        this.preparerTranslatorUpdater.initialise();
        this.certificationUpdater.initialise();
        this.documentsUpdater.initialise();
        this.documentChangeTrackingService.initialise();
    }

    componentWillUnmount() {
        this.applicationInfoInterceptor.cleanup();
        // logic layer: temporary place to clean up the updaters
        this.candidateDetailsUpdater.cleanup();
        this.preparerTranslatorUpdater.cleanup();
        this.certificationUpdater.cleanup();
        this.documentsUpdater.cleanup();
        this.documentChangeTrackingService.cleanup();
    }

    private async initAsync() {
        this.configurationValuesService = ConfigurationValuesService.getInstance();
        const configServiceOk = await this.configurationValuesService.initAsync();
        const translationServiceOk = await TranslationService.getInstance().initAsync();

        const partnerId = this.configurationValuesService?.partnerId ?? '';
        registerAppConstantsProvider(() => ({
            APP_TYPE: 'remote-representative',
            ENVIRONMENT: '',
            PARTNER_ID: partnerId,
            COMPANY_ID: ''
        }));

        this.trackingService = new TrackingService();
        this.trackingService.initPiwik({
            url: this.configurationValuesService.piwikUrl,
            siteId: this.configurationValuesService.piwikTrackingId
        });

        i18n.on('languageChanged', () => this.forceUpdate());

        if (configServiceOk) {
            Sentry.init({
                dsn: this.configurationValuesService.sentryDsnUrl,
                environment: this.configurationValuesService.sentryEnvironment,
                integrations: [new BrowserTracing()],

                // Set tracesSampleRate to 1.0 to capture 100%
                // of transactions for performance monitoring.
                // We recommend adjusting this value in production
                tracesSampleRate: this.configurationValuesService.tracesSampleRate
            });

            Sentry.setTag('partnerID', partnerId);
        }

        await this.setState({
            error: !configServiceOk || !translationServiceOk,
            loaded: true
        });
    }

    public render() {
        const {
            error,
            loaded
        } = this.state;

        if (!loaded) {
            return <div/>;
        }

        const baseRoutePath = Routes.Authorisation;
        const basename = App.extractBasenameFromPath(baseRoutePath);

        /**
         * Note: the routing works fine as soon as the browser router takes over.
         * It doesn't work going to a route directly, however as we want all
         * authenticated sessions to start from the pin code page anyway,
         * it should be fine for now
         */
        return (
            <BrowserRouter basename={basename}>
                {this.renderApp(error, baseRoutePath)}
            </BrowserRouter>
        );
    }

    private renderApp(error: boolean, routePath: string) {
        // This could be provided from the router later
        const searchQuery = window.location.search;
        const authenticationParamsProvider: ComponentAndPropEntry<typeof AuthenticationParamsProvider> = [
            AuthenticationParamsProvider,
            { query: searchQuery }
        ];
        return (
            <ComposeProviders components={[
                [MuiPickersUtilsProvider, { utils: MomentUtils }],
                authenticationParamsProvider,
                AuthorisationProvider,
                BrandingProvider
            ]}>
                <PageViewTracker/>
                <BrandedHeader additionalContent={<LanguageSelector/>}/>
                {error}
                {
                    !error &&
                    <div className='content-wrapper'>
                        <div className='wrapper'>
                            <Switch>
                                <AuthorisedRoute exact={true}
                                                 path={`${routePath}/section2/upload-documents`}
                                                 component={I9DocumentUpload}/>
                                <AuthorisedRoute exact={true}
                                                 path={`${routePath}/section2/documents`}
                                                 component={I9DocumentReview}/>
                                <AuthorisedRoute exact={true}
                                                 path={`${routePath}/section2/employee`}
                                                 component={I9EmployeeInformationReview}/>
                                <AuthorisedRoute exact={true}
                                                 path={`${routePath}/section2/completeI9`}
                                                 component={CompleteFormI9Section2}/>
                                <AuthorisedRoute exact={true}
                                                 path={`${routePath}/section2/thankyou`}
                                                 component={Section2ThankYou}/>
                                <AuthorisedRoute exact={true}
                                                 path={`${routePath}/section3/review`}
                                                 component={Review}/>
                                {/*for testing: change the component below to the component page to be tested */}
                                <Route exact={true}
                                       path={routePath}
                                       component={PinCodeAuthorisation}/>
                                <Route component={NotFoundContainer}/>
                            </Switch>
                        </div>
                        <Loader/>
                    </div>
                }
                {
                    error &&
                    <ErrorPage/>
                }
            </ComposeProviders>
        );
    }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.tsx